
import { computed, defineComponent } from 'vue';
import { useRouter } from 'vue-router/composables';

import { t, te } from '~i18n';
import sanitize from '~plugin/sanitize';
import store from '~store';

interface MixedApiError {
  code: string;
  message?: string;
  context: {
    value: string;
    currency: string;
    shopName: string;
    realCategoryName: string;
    realRedeemUrl: string;
    partner: string;
    paymentProvider: string;
  };
}

type MixedApiErrorMessage = string | ((err: MixedApiError) => string);

interface MixedApiErrorMessages {
  [key: string]: MixedApiErrorMessage;
}

export default defineComponent({
  props: {
    /* Error object or array from API */
    error: {
      type: [Object, Array],
      default: null,
    },
  },

  setup(props) {
    const router = useRouter();
    const { getters } = store;

    const supportUrl = computed(() => getters.getSupportData?.url);
    const overrideApiErrorMessageHtml = computed(() => getters.getOverrideApiErrorMessageHtml);

    const errors = computed(() => {
      if (Array.isArray(props.error)) {
        return props.error;
      }

      return [props.error];
    });

    /*
     * Get a combined response for Error RDMX9 and RDMX10
     */
    function RDMx9and10response(err: MixedApiError) {
      let res = '';
      if (err?.context?.partner === 'ssc') {
        res += `${t('redeem.contactSteuerSparcard')} `;
      }
      if (err?.context?.partner === 'msk') {
        res += `${t('redeem.contactMeinShoppingKonto')}`;
      }
      return res;
    }

    const messages = computed(() => ({
      // standard
      STDx1: t('wgs.std.error.occurred'),
      STDx2: t('wgs.std.error.occurredAskSupport'),
      STDx18: t('wgs.captcha.invalid'),
      STDx19: t('wgs.std.paymentFailedTryLater'),
      STDx21: (err: MixedApiError) => t('checkout.payment.paymentFailedChooseOtherProvider', {
        paymentProvider: err?.context?.paymentProvider,
      }),

      // voucher
      VCRx1: t('wgs.std.voucherNotFound'),
      VCRx2: t('wgs.std.error.occurredAskSupport'),
      VCRx4: t('wgs.std.voucherAlreadyRedeemed'),
      VCRx3: (err: MixedApiError) => t('wgs.std.minimalVoucherChargeValueNotReached', {
        value: err?.context?.value,
        currency: err?.context?.currency,
      }),
      VCRx5: t('wgs.std.voucherCreditInsufficient'),
      VCRx6: `${t('wgs.std.error.occurred')}.${t('wgs.std.error.tryAgain')}`,
      VCRx7: t('redeem.codeTester.partnerNotAvailable'),
      VCRx8: t('redeem.codeTester.voucherNotActivated'),
      VCRx9: t('redeem.codechecker.voucherNoLongerValid', { supportUrl: supportUrl.value }),
      VCRx10: t('redeem.codechecker.voucherCancelled'),
      VCRx11: t('wgs.std.error.occurredAskSupport'),
      VCRx13: () => {
        let res = '';
        res += `${t('redeem.codechecker.voucherOldCodeFormat_intro')}`;
        res += '<br /><br />';
        res += t('redeem.codechecker.voucherOldCodeFormat_expired', {
          link: router.resolve({ name: 'voucherReactivation' }).href,
        });
        res += '<br /><br />';
        res += `${t('redeem.thankYou.ihrWGTeam')}`;
        return res;
      },
      VCRx14: t('wgs.std.error.occurredAskSupport'),
      VCRx17: t('redeem.prepayhelper.DeEingeCoKaNicAufgeWer'),
      VCRx18: t('redeem.prepayhelper.DeEingeGutsIsBerAufge'),
      VCRx19: t('redeem.prepayhelper.DieGutsBefiSiBerIWare'),
      VCRx20: (err: MixedApiError) => t('redeem.wrongCategoryRealCategoryAddress', {
        realRedeemUrl: err?.context?.realRedeemUrl,
      }),
      VCRx29: t('wgs.std.error.sscCode'),
      VCRx44: t('redeem.voucherhelper.invalidcode'),
      VCRx53: t('help.reactivateVoucher.voucherNotLocked'),

      // redeem
      RDMx1: t('redeem.redeemLink.noLongerValid', {
        supportUrl: supportUrl.value,
      }),
      RDMx4: t('redeem.redeemLink.noLongerValid'),
      RDMx5: t('wgs.std.error.occurredAskSupport'),
      RDMx9: (err: MixedApiError) => RDMx9and10response(err),
      RDMx10: (err: MixedApiError) => RDMx9and10response(err),

      // MRC
      MRCx1: (err: MixedApiError) => {
        let res = '';
        res += t('redeem.redeemLink.codeNotJetAvailableHeading');
        res += '<br /><br />';
        res += t('redeem.redeemLink.codeNotJetAvailableText', { shop: err?.context?.shopName });
        return res;
      },

      // shop
      SHPx1: t('redeem.redeemLink.shopNichtMehrVerf'),
      ORDx1: t('app.appbundle.errorOrderNumberNotFound'),
      ORDx2: t('app.appbundle.OrderPartsSend'),
    }) as MixedApiErrorMessages);

    /*
     * Sanitize a dangerous HTML String to a safe one
     */
    function safeHtml(html: string) {
      const rules = {
        allowedTags: [
          // List Elements
          'ol',
          'ul',
          'li',

          // Strong
          'strong',
          'b',

          // Paragraphs and line breaks
          'p',
          'br',

          // Italic
          'i',

          // Font Element
          'font',

          // Inline Text
          'span',

          // Link
          'a',

          // More ...
          'u',
        ],

        allowedAttributes: {
          font: ['style', 'color'],
          a: ['href', 'no-relocate'],
        },
      };

      return sanitize.prototype.$sanitize(html, rules);
    }

    /*
     * Return the Error Message as (safe) HTML-Code.
     * If a function or string exists as a message, set it as error response message.
     * If not, return the translation fallback or the default wgs std error message.
     */
    function getErrorMessageHtml(err: MixedApiError) {
      const message = messages?.value?.[err.code] as MixedApiErrorMessage;
      const fallback = `errorCode.${err?.code}`;
      let response = '';

      switch (typeof message) {
        case 'function':
          response = message(err);
          break;

        case 'string':
          response = message;
          break;

        default:
          response = t(te(fallback) ? fallback : 'wgs.std.error.occurred');
          break;
      }

      if (overrideApiErrorMessageHtml.value) {
        response = overrideApiErrorMessageHtml.value;
      }

      return safeHtml(`${response} (${err.code || err.message})`);
    }

    return {
      errors,
      messages,

      getErrorMessageHtml,
    };
  },
});
